import { inject } from 'vue';

function replaceTokens(message, tokens) {
  if (!tokens) {
    return message;
  }
  return Object.keys(tokens).reduce(
    (acc, token) => acc.replace(`{${token}}`, tokens[token].toString()),
    message
  );
}

const key = Symbol("i18n");

const useI18n = () => {
  const i18n = inject(key, {
    t: (strringKey, tokens) => replaceTokens(strringKey, tokens) || strringKey,
    tc: (stringKey, _count, tokens) => replaceTokens(stringKey, tokens) || stringKey
  });
  return i18n;
};

var AgentActionKind;
(function (AgentActionKind) {
    /**
     * Action to be called when applying a discount code (of any kind, not just of type "promocode").
     *
     * It corresponds to the Nostromo type `ButtonActionKind.ApplyPromoCode`.
     */
    AgentActionKind["ApplyDiscount"] = "apply-discount";
    /**
     * Action to be called when a UI/view is definetively closed from the [x] button
     * (or by a backdrop click) without having engaged with the contents of the message.
     */
    AgentActionKind["Dismiss"] = "dismiss";
    /**
     * Action to be called when a user positively engages with the UI/view, for instance
     * by executing a CTA. This is an exclusively disjunctive action with "dismiss", either
     * one or the other can happen, never both.
     */
    AgentActionKind["Engage"] = "engage";
    /**
     * Action to be called when a UI/view is closed.
     */
    AgentActionKind["Hide"] = "hide";
    /**
     * This action is usually called in replacemend of a CTA action
     * (like ApplyDiscount, OpenBookingEngine, OpenUri) when those actions
     * do not have a handler defined.
     *
     * Additionally is used by the Nostromo type `ButtonActionKind.CloseTool` on Inliners.
     */
    AgentActionKind["NoAction"] = "no-action";
    /**
     * Action that should never be called, is a Noop.
     */
    AgentActionKind["Fallback"] = "none";
    /**
     * Action to be called when a click happends on _any_ generic link of the contents.
     * By 'generic link' we mean a link not formally defined in the data struct, but just part of a
     * rich text/html of the message. This action should not be called when for clicks on buttons/CTAs.
     */
    AgentActionKind["GenericLinkClick"] = "generic-link-click";
    /**
     * Action to be called when loading a custom JS script.
     */
    AgentActionKind["LoadScript"] = "load-script";
    /**
     * Action to be called when opening the Booking Engine page, either with the default search data,
     * or with the last search data.
     *
     * It corresponds to the Nostromo type `ButtonActionKind.OpenBookingEngine`,
     * and `ButtonActionKind.LastSearch`.
     */
    AgentActionKind["OpenBookingEngine"] = "open-be";
    /**
     * Action to be called when opening a URI (either http(s), mail or tel).
     *
     * It corresponds to the Nostromo types `ButtonActionKind.OpenUrl`,
     * `ButtonActionKind.OpenMailClient` and `ButtonActionKind.CallPhoneNumber`.
     */
    AgentActionKind["OpenUri"] = "open-uri";
    /**
     * Action to be called to classify the visitor in an Audience group.
     */
    AgentActionKind["PredictiveAudience"] = "predictive-audience";
    /**
     * Action to be called when sending a (custom) form to the Site.
     *
     * It corresponds to the Nostromo type `ToolModuleKind.Form`.
     */
    AgentActionKind["SendForm"] = "send-form";
    /**
     * Action to be called when the visitor is sending a save-your-search email to himself/herself.
     * It corresponds to the Nostromo types `ToolModuleKind.SavedSearch` and
     * `ButtonActionKind.SavedSearch`.
     */
    AgentActionKind["SendSaveSearch"] = "send-save-search";
    /**
     * Action to be called when sending a contact email to the Site.
     */
    AgentActionKind["SendSiteContact"] = "send-site-contact";
    /**
     * Action to be called when subscribing to a mailing list, newsletter or similar.
     *
     * It corresponds to the Nostromo type `ToolModuleKind.EmailCapture`.
     */
    AgentActionKind["SendSubscribe"] = "send-subscribe";
    /**
     * Action to be called when a UI is displayed.
     */
    AgentActionKind["Show"] = "show";
    /**
     * Action to be called when the visitor is signing-in or signing-up with with THN.
     *
     * It corresponds to the Nostromo type `ToolModuleKind.Login`.
     */
    AgentActionKind["Login"] = "login";
    /**
     * Action to be called when a message/tool that needs to "move" the full body content is displayed.
     */
    AgentActionKind["TranslateDocumentBody"] = "translate-document-body";
})(AgentActionKind || (AgentActionKind = {}));
var AgentActionLoginStep;
(function (AgentActionLoginStep) {
    AgentActionLoginStep["Resend"] = "resend";
    AgentActionLoginStep["Signup"] = "signup";
    AgentActionLoginStep["Signin"] = "signin";
    AgentActionLoginStep["Validation"] = "validation";
})(AgentActionLoginStep || (AgentActionLoginStep = {}));

var Locale;
(function (Locale) {
    Locale["Arabic"] = "ar";
    Locale["Basque"] = "eu";
    Locale["Bulgarian"] = "bg";
    Locale["Catalan"] = "ca";
    Locale["Chinese"] = "zh";
    Locale["ChineseTraditional"] = "zh-TW";
    Locale["Croatian"] = "hr";
    Locale["Czech"] = "cs";
    Locale["Danish"] = "da";
    Locale["Deutsch"] = "de";
    Locale["Dutch"] = "nl";
    Locale["English"] = "en";
    Locale["Estonian"] = "et";
    Locale["Finnish"] = "fi";
    Locale["French"] = "fr";
    Locale["Greek"] = "el";
    Locale["Hebrew"] = "he";
    Locale["Hungarian"] = "hu";
    Locale["Icelandic"] = "is";
    Locale["Indonesian"] = "id";
    Locale["Italian"] = "it";
    Locale["Japanese"] = "ja";
    Locale["Korean"] = "ko";
    Locale["Lithuanian"] = "lt";
    Locale["Norwegian"] = "no";
    Locale["Polish"] = "pl";
    Locale["Portuguese"] = "pt";
    Locale["Romanian"] = "ro";
    Locale["Russian"] = "ru";
    Locale["Slovenian"] = "sl";
    Locale["Spanish"] = "es";
    Locale["Swedish"] = "sv";
    Locale["Thai"] = "th";
    Locale["Turkish"] = "tr";
    Locale["Ukrainian"] = "uk";
    Locale["Vietnamese"] = "vi";
})(Locale || (Locale = {}));

var CustomRulesSection;
(function (CustomRulesSection) {
    CustomRulesSection["Timing"] = "timing";
    CustomRulesSection["Oraculo"] = "oraculo";
    CustomRulesSection["VisitorProfile"] = "visitorProfile";
    CustomRulesSection["VisitorBehaviour"] = "visitorBehaviour";
    CustomRulesSection["Search"] = "search";
    CustomRulesSection["TravelParty"] = "travelParty";
    CustomRulesSection["DataVariables"] = "dataVariables";
    CustomRulesSection["Demand"] = "demand";
    CustomRulesSection["Integrations"] = "integrations";
})(CustomRulesSection || (CustomRulesSection = {}));
var ConditionKind;
(function (ConditionKind) {
    ConditionKind["Exclude"] = "exclude";
    ConditionKind["Include"] = "include";
})(ConditionKind || (ConditionKind = {}));
var Operator;
(function (Operator) {
    Operator["Any"] = "any";
    Operator["Is"] = "is";
    Operator["Are"] = "are";
    Operator["IsBetween"] = "isBetween";
    Operator["AreBetween"] = "areBetween";
    Operator["IsLower"] = "isLower";
    Operator["AreLower"] = "areLower";
    Operator["IsHigher"] = "isHigher";
    Operator["IsGreater"] = "isGreater";
    Operator["AreGreater"] = "areGreater";
    Operator["IsBefore"] = "isBefore";
    Operator["AreBefore"] = "areBefore";
    Operator["IsAfter"] = "isAfter";
    Operator["AreAfter"] = "areAfter";
    Operator["Contain"] = "contain";
})(Operator || (Operator = {}));
var DataVariableRuleOperator;
(function (DataVariableRuleOperator) {
    DataVariableRuleOperator["Is"] = "is";
    DataVariableRuleOperator["IsNot"] = "isNot";
    DataVariableRuleOperator["AnyValue"] = "anyValue";
    DataVariableRuleOperator["NotAnyValue"] = "notAnyValue";
    DataVariableRuleOperator["Lower"] = "lower";
    DataVariableRuleOperator["Greater"] = "greater";
    DataVariableRuleOperator["Contains"] = "contains";
    DataVariableRuleOperator["NotContains"] = "notContains";
})(DataVariableRuleOperator || (DataVariableRuleOperator = {}));
/**
 * The visitor registration status in the website own credentials system.
 */
var VisitorStatus;
(function (VisitorStatus) {
    /**
     * @alias VisitorStatus.RegisteredAndLogged
     * @deprecated
     */
    VisitorStatus["Registered"] = "registered";
    /**
     * @alias VisitorStatus.NotLogged
     * @deprecated
     */
    VisitorStatus["NonRegistered"] = "non_registered";
    /**
     * The visitor is currently logged-in. Implicitly the visitor needs to be registered,
     * however there should be no strict check about registration status on implementations.
     */
    VisitorStatus["RegisteredAndLogged"] = "registered";
    /**
     * The visitor is not registered. This implicitly means the visitor is also
     * NOT currently logged-in, as being logged-in would imply registration.
     */
    VisitorStatus["NotRegisteredAndNotLogged"] = "non_registered_nor_logged";
    /**
     * The visitor is registered. If the visitor were to be currently logged-in that (too)
     * would imply It is registered.
     */
    VisitorStatus["RegisteredOrLogged"] = "registered_all";
    /**
     * The visitor is registered AND is not currently logged-in.
     */
    VisitorStatus["RegisteredAndNotLogged"] = "registered_non_logged";
    /**
     * The visitor is currently not logged-in, disregarding registration status.
     */
    VisitorStatus["NotLogged"] = "non_registered";
})(VisitorStatus || (VisitorStatus = {}));
// This is for the thn/instant login system
var VisitorLogin;
(function (VisitorLogin) {
    VisitorLogin["Logged"] = "logged";
    VisitorLogin["NonLogged"] = "nonLogged";
})(VisitorLogin || (VisitorLogin = {}));
var VisitorBehaviourInteractionType;
(function (VisitorBehaviourInteractionType) {
    VisitorBehaviourInteractionType["Any"] = "any";
    VisitorBehaviourInteractionType["First"] = "first";
    VisitorBehaviourInteractionType["All"] = "all";
    VisitorBehaviourInteractionType["Search"] = "search";
    VisitorBehaviourInteractionType["Booked"] = "booked";
    VisitorBehaviourInteractionType["Custom"] = "custom";
})(VisitorBehaviourInteractionType || (VisitorBehaviourInteractionType = {}));

var MainStyle;
(function (MainStyle) {
    MainStyle["Colors"] = "colors";
    MainStyle["Fonts"] = "fonts";
    MainStyle["Button"] = "button";
    MainStyle["SecondaryButton"] = "secondaryButton";
    MainStyle["Background"] = "background";
    MainStyle["Overlay"] = "overlay";
})(MainStyle || (MainStyle = {}));
// FONTS
var TextTransform;
(function (TextTransform) {
    TextTransform["None"] = "none";
    TextTransform["Uppercase"] = "uppercase";
    TextTransform["Lowercase"] = "lowercase";
    TextTransform["Titlecase"] = "titlecase";
})(TextTransform || (TextTransform = {}));
var FontType;
(function (FontType) {
    FontType["Google"] = "google";
    FontType["File"] = "file";
    FontType["Regular"] = "regular";
})(FontType || (FontType = {}));
// BUTTONS
var RadiusKind;
(function (RadiusKind) {
    RadiusKind["Custom"] = "custom";
    RadiusKind["Square"] = "square";
    RadiusKind["Medium"] = "medium";
    RadiusKind["Rounded"] = "rounded";
})(RadiusKind || (RadiusKind = {}));
var MeasureUnit;
(function (MeasureUnit) {
    MeasureUnit["Percentage"] = "%";
    MeasureUnit["Pixels"] = "px";
    MeasureUnit["Rem"] = "rem";
    MeasureUnit["Em"] = "em";
})(MeasureUnit || (MeasureUnit = {}));
var ButtonStyleKind;
(function (ButtonStyleKind) {
    ButtonStyleKind["Solid"] = "solid";
    ButtonStyleKind["Outline"] = "outline";
})(ButtonStyleKind || (ButtonStyleKind = {}));

var TermsLinkKind;
(function (TermsLinkKind) {
    TermsLinkKind["Url"] = "url";
    TermsLinkKind["DefaultText"] = "default_text";
    TermsLinkKind["CustomText"] = "custom_text";
    TermsLinkKind["NotLinked"] = "not_linked";
    TermsLinkKind["CustomUrl"] = "custom_url";
})(TermsLinkKind || (TermsLinkKind = {}));

var ActivityKind;
(function (ActivityKind) {
    ActivityKind["NowViewing"] = "nowViewing";
    ActivityKind["TodayViewed"] = "todayViewed";
    ActivityKind["BookedToday"] = "bookedToday";
    ActivityKind["LastReservation"] = "lastReservation";
    ActivityKind["DiscountToday"] = "discountToday";
    ActivityKind["TodaySpecialOffer"] = "todaySpecialOffer";
})(ActivityKind || (ActivityKind = {}));

var ButtonActionKind;
(function (ButtonActionKind) {
    ButtonActionKind["ApplyPromoCode"] = "apply_promo_code";
    ButtonActionKind["OpenUrl"] = "open_url";
    ButtonActionKind["OpenMailClient"] = "open_mail_client";
    ButtonActionKind["CallPhoneNumber"] = "call_phone_number";
    ButtonActionKind["CloseTool"] = "close_tool";
    ButtonActionKind["EmailCapture"] = "email_capture";
    ButtonActionKind["SavedSearch"] = "saved_search";
    ButtonActionKind["GoMainStep"] = "go_main_step";
    ButtonActionKind["SendForm"] = "form";
    ButtonActionKind["OpenBookingEngine"] = "send_to_be";
    ButtonActionKind["Submit"] = "submit";
    ButtonActionKind["LastSearch"] = "send_to_last_search";
})(ButtonActionKind || (ButtonActionKind = {}));
var DiscountCodeKind;
(function (DiscountCodeKind) {
    DiscountCodeKind["Default"] = "default";
    DiscountCodeKind["Coupon"] = "coupon";
    DiscountCodeKind["Rate"] = "rate";
    DiscountCodeKind["Corporative"] = "corpo";
    DiscountCodeKind["Group"] = "group";
})(DiscountCodeKind || (DiscountCodeKind = {}));
var ButtonKind;
(function (ButtonKind) {
    ButtonKind["Primary"] = "primary";
    ButtonKind["Secondary"] = "secondary";
})(ButtonKind || (ButtonKind = {}));

var ToolKind;
(function (ToolKind) {
    ToolKind["Note"] = "note";
    ToolKind["Layer"] = "layer";
    ToolKind["Exit"] = "exit";
    ToolKind["Inliner"] = "inliner";
})(ToolKind || (ToolKind = {}));
// @deprecated ?
var DefaultStep;
(function (DefaultStep) {
    DefaultStep["Success"] = "success";
    DefaultStep["Failure"] = "failure";
    DefaultStep["Main"] = "main";
})(DefaultStep || (DefaultStep = {}));
var LoginStep;
(function (LoginStep) {
    LoginStep["LoginSignin"] = "login-signin";
    LoginStep["LoginRegister"] = "login-register";
    LoginStep["LoginSuccess"] = "login-success";
})(LoginStep || (LoginStep = {}));

var ToolModuleKind;
(function (ToolModuleKind) {
    ToolModuleKind["Activity"] = "activity";
    ToolModuleKind["Button"] = "button";
    ToolModuleKind["Countdown"] = "countdown";
    ToolModuleKind["EmailCapture"] = "emailCapture";
    ToolModuleKind["Form"] = "form";
    ToolModuleKind["Heading"] = "heading";
    ToolModuleKind["ImageWithBehaviour"] = "imageWithBehaviour";
    ToolModuleKind["Icon"] = "icon";
    ToolModuleKind["Paragraph"] = "paragraph";
    ToolModuleKind["Perks"] = "perks";
    ToolModuleKind["SavedSearch"] = "savedSearch";
    ToolModuleKind["Spacer"] = "spacer";
    ToolModuleKind["Login"] = "login";
})(ToolModuleKind || (ToolModuleKind = {}));

var PerkTooltipKind;
(function (PerkTooltipKind) {
    PerkTooltipKind["None"] = "none";
    PerkTooltipKind["WebExclusive"] = "web_exclusive";
    PerkTooltipKind["SubjectToAvailibity"] = "subject_to_availability";
    PerkTooltipKind["OnlyForSpecificRates"] = "only_for_specific_rates";
    PerkTooltipKind["OnlyForRegisterdUsers"] = "only_for_registered_users";
})(PerkTooltipKind || (PerkTooltipKind = {}));
var PerkLayoutKind;
(function (PerkLayoutKind) {
    PerkLayoutKind["Bullets"] = "bullets";
    PerkLayoutKind["Highlights"] = "highlights";
})(PerkLayoutKind || (PerkLayoutKind = {}));

var CountdownStyle;
(function (CountdownStyle) {
    CountdownStyle["None"] = "none";
    CountdownStyle["Solid"] = "solid";
    CountdownStyle["Outline"] = "outline";
})(CountdownStyle || (CountdownStyle = {}));
var CountdownSize;
(function (CountdownSize) {
    CountdownSize["Normal"] = "normal";
    CountdownSize["Compact"] = "compact";
})(CountdownSize || (CountdownSize = {}));
var CountdownKind;
(function (CountdownKind) {
    CountdownKind["Date"] = "date";
    CountdownKind["Time"] = "time";
})(CountdownKind || (CountdownKind = {}));
var CountdownTimezone;
(function (CountdownTimezone) {
    CountdownTimezone["Visitor"] = "visitor";
    CountdownTimezone["Hotel"] = "hotel";
})(CountdownTimezone || (CountdownTimezone = {}));

var FormFieldsPosition;
(function (FormFieldsPosition) {
    FormFieldsPosition["Out"] = "out";
    FormFieldsPosition["Inside"] = "inside";
})(FormFieldsPosition || (FormFieldsPosition = {}));
var FormSelectionKind;
(function (FormSelectionKind) {
    FormSelectionKind["Single"] = "single";
    FormSelectionKind["Multiple"] = "multiple";
})(FormSelectionKind || (FormSelectionKind = {}));
var FormFieldKind;
(function (FormFieldKind) {
    FormFieldKind["ShortText"] = "shortText";
    FormFieldKind["Email"] = "email";
    FormFieldKind["Phone"] = "phone";
    FormFieldKind["Date"] = "date";
    FormFieldKind["LongText"] = "longText";
    FormFieldKind["DropDown"] = "dropdown";
    FormFieldKind["Selection"] = "selection";
})(FormFieldKind || (FormFieldKind = {}));

var ToolPosition;
(function (ToolPosition) {
    ToolPosition["Top"] = "T";
    ToolPosition["Bottom"] = "B";
    ToolPosition["Left"] = "L";
    ToolPosition["Right"] = "R";
    ToolPosition["UpperLeft"] = "UL";
    ToolPosition["UpperRight"] = "UR";
    ToolPosition["UpperMidLeft"] = "UML";
    ToolPosition["UpperMidRight"] = "UMR";
    ToolPosition["LowerMidLeft"] = "LML";
    ToolPosition["LowerMidRight"] = "LMR";
    ToolPosition["LowerLeft"] = "LL";
    ToolPosition["LowerRight"] = "LR";
})(ToolPosition || (ToolPosition = {}));
var DeviceKind;
(function (DeviceKind) {
    DeviceKind["Mobile"] = "mobile";
    DeviceKind["Desktop"] = "desktop";
})(DeviceKind || (DeviceKind = {}));

var TimingFrequency;
(function (TimingFrequency) {
    TimingFrequency["EveryPage"] = "on_every_page";
    TimingFrequency["OncePerVisit"] = "once_per_visit";
    TimingFrequency["OncePerVisitor"] = "once_per_visitor";
    TimingFrequency["OncePerProperty"] = "once_per_property";
    TimingFrequency["OncePerBrand"] = "once_per_brand";
})(TimingFrequency || (TimingFrequency = {}));

var TriggerKind;
(function (TriggerKind) {
    TriggerKind["Load"] = "load";
    TriggerKind["Scroll"] = "scroll";
    TriggerKind["Idle"] = "idle";
})(TriggerKind || (TriggerKind = {}));

var ToolSize;
(function (ToolSize) {
    ToolSize["Regular"] = "regular";
    ToolSize["Compact"] = "compact";
})(ToolSize || (ToolSize = {}));
var LocaleImageKind;
(function (LocaleImageKind) {
    LocaleImageKind["Single"] = "single";
    LocaleImageKind["Multi"] = "multi";
})(LocaleImageKind || (LocaleImageKind = {}));
var ToolLayoutKind;
(function (ToolLayoutKind) {
    ToolLayoutKind["Bar"] = "bar";
    ToolLayoutKind["Circular"] = "circular";
    ToolLayoutKind["CustomImage"] = "custom-image";
    ToolLayoutKind["Fullscreen"] = "full-screen";
    ToolLayoutKind["FullscreenMini"] = "full-screen-mini";
    ToolLayoutKind["LowerNotification"] = "lower-notification";
    ToolLayoutKind["NotificationTab"] = "notification-tab";
    ToolLayoutKind["Sidebar"] = "sidebar";
    ToolLayoutKind["SideGradient"] = "side-gradient";
    ToolLayoutKind["SlideIn"] = "slide-in";
    ToolLayoutKind["Square"] = "square";
    ToolLayoutKind["SquareImage"] = "square-image";
    ToolLayoutKind["Sticky"] = "sticky";
    ToolLayoutKind["StickyImage"] = "sticky-image";
})(ToolLayoutKind || (ToolLayoutKind = {}));
var ToolContentAlignment;
(function (ToolContentAlignment) {
    ToolContentAlignment["Left"] = "left";
    ToolContentAlignment["Center"] = "center";
    ToolContentAlignment["Right"] = "right";
})(ToolContentAlignment || (ToolContentAlignment = {}));
var ToolEntryBehaviour;
(function (ToolEntryBehaviour) {
    ToolEntryBehaviour["Inline"] = "inline";
    ToolEntryBehaviour["Overlay"] = "overlay";
})(ToolEntryBehaviour || (ToolEntryBehaviour = {}));

({
    [ActivityKind.NowViewing]: 'There are XXXXXX people looking at this hotel right now',
    [ActivityKind.TodayViewed]: 'XXXXXX people have looked at this hotel today',
    [ActivityKind.BookedToday]: 'XXXXXX people have already booked today',
    [ActivityKind.LastReservation]: 'Last reservation was XXXXXX hours ago',
    [ActivityKind.DiscountToday]: 'XXXXXX% discount only today',
    [ActivityKind.TodaySpecialOffer]: 'Today\'s special offer: XXXXXX',
});
({
    [PerkTooltipKind.None]: 'none',
    [PerkTooltipKind.SubjectToAvailibity]: 'Subject to availability',
    [PerkTooltipKind.WebExclusive]: 'Web exclusive',
    [PerkTooltipKind.OnlyForRegisterdUsers]: 'Only for registered users',
    [PerkTooltipKind.OnlyForSpecificRates]: 'Only for specific rates',
});

export { useI18n as u };
//# sourceMappingURL=widgets-core.es.C7uAbUpp.js.map
